import React, { useState } from 'react';

import { Document as PDFDocument, Page, pdfjs } from 'react-pdf';

import { Download, ExternalLink } from 'lucide-react';

import Loader from './Loader';

// Set up PDF.js worker - use CDN fallback
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface Document {
	id: string;
	name: string;
	type: string;
	date: string;
	size: string;
	status: string;
	processingMonth: string;
	pdfUrl?: string;
	results?: any;
}

interface DocumentViewerProps {
	document: Document;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({ document }) => {
	const [numPages, setNumPages] = useState<number | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
		setNumPages(numPages);
		setLoading(false);
		setError(false);
	};

	const onDocumentLoadError = () => {
		setError(true);
		setLoading(false);
	};

	return (
		<div className="h-full flex flex-col bg-gray-100">
			{/* PDF Content */}
			<div className="flex-1 overflow-hidden bg-gray-100">
				{document.pdfUrl && !error ? (
					<div className="h-full flex flex-col">
						{/* PDF Header */}
						{numPages && (
							<div className="flex items-center justify-between p-3 bg-white border-b border-gray-200">
								<div className="flex items-center space-x-2">
									<span className="text-sm text-gray-600">
										{numPages} page{numPages !== 1 ? 's' : ''}
									</span>
								</div>
								<a
									href={document.pdfUrl}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
								>
									<Download className="w-4 h-4 mr-1" />
									Download
								</a>
							</div>
						)}

						{/* PDF Viewer with Scrolling */}
						<div className="flex-1 overflow-auto p-4">
							{loading && (
								<div className="flex items-center justify-center h-full">
									<Loader size="md" text="Loading PDF..." />
								</div>
							)}

							<div className="flex flex-col items-center space-y-4">
								<PDFDocument
									file={document.pdfUrl}
									onLoadSuccess={onDocumentLoadSuccess}
									onLoadError={onDocumentLoadError}
									loading={
										<div className="flex items-center justify-center h-64">
											<Loader size="md" text="Loading PDF..." />
										</div>
									}
								>
									{Array.from(new Array(numPages), (el, index) => (
										<div key={`page_${index + 1}`} className="mb-4">
											<Page
												pageNumber={index + 1}
												width={Math.min(600, window.innerWidth * 0.8)}
												renderTextLayer={false}
												renderAnnotationLayer={false}
											/>
											{numPages && numPages > 1 && (
												<div className="text-center mt-2 text-sm text-gray-500">
													Page {index + 1} of {numPages}
												</div>
											)}
										</div>
									))}
								</PDFDocument>
							</div>
						</div>
					</div>
				) : (
					<div className="flex items-center justify-center h-full text-gray-500">
						<div className="text-center">
							{document.pdfUrl ? (
								<>
									<p className="text-lg font-semibold mb-2">
										PDF Preview Unavailable
									</p>
									<p className="text-sm mb-4">Unable to load PDF in browser.</p>
									<a
										href={document.pdfUrl}
										target="_blank"
										rel="noopener noreferrer"
										className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
									>
										<ExternalLink className="w-4 h-4 mr-2" />
										Open PDF in New Tab
									</a>
								</>
							) : (
								<>
									<p className="text-lg font-semibold mb-2">No PDF Available</p>
									<p className="text-sm">
										The PDF for this document is not available.
									</p>
								</>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default DocumentViewer;
